import { FC, ReactNode } from 'react'

import { Item, Indicator } from '@radix-ui/react-radio-group'
import { styled } from 'styled-components'

import { onSurface, onSurfaceVariant, outline, surfaceBright } from 'core/styles/tokens'

type RadioItemProps = {
  className?: string
  /** The unique string to pass to `RadioGroup`s `onChange` prop. */
  value: string
  /** The description of the radio item */
  description?: string
  /** Disables all interactivity while true. */
  disabled?: boolean
  /** The text to display alongside the checkbox. */
  label?: ReactNode
  /** The text for accessibility for when `label` is not used. */
  ariaLabel?: string
  /** The variant of the radio item. */
  variant?: 'default' | 'compact'
}

/** A single radio input. Use with `RadioGroup`. */
const RadioItem: FC<RadioItemProps> = ({
  value,
  description,
  disabled,
  label,
  ariaLabel,
  className,
  variant = 'default',
}) => (
  <Box $disabled={disabled} className={className}>
    <StyledItem $variant={variant} aria-label={ariaLabel} disabled={disabled} id={value} value={value}>
      <StyledIndicator $variant={variant} forceMount />
    </StyledItem>

    {!!label && (
      <Label $variant={variant} htmlFor={value}>
        {label}
        {!!description && <Description>{description}</Description>}
      </Label>
    )}
  </Box>
)

export default RadioItem

const Box = styled.div<{ $disabled: boolean | undefined }>`
  display: flex;
  gap: 10px;
  align-items: center;

  button,
  label {
    cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  }

  [data-disabled] & button,
  [data-disabled] & label {
    cursor: default;
  }
`

const StyledItem = styled(Item)<{ $variant?: 'default' | 'compact' }>`
  box-sizing: border-box;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 2px solid ${outline};
  border-radius: 100%;
  background-color: ${surfaceBright};
  width: 20px;
  height: 20px;

  &[data-state='checked'] {
    border-color: ${(p) => p.theme.primary};
    background-color: ${(p) => p.theme.secondary};
  }

  &[data-disabled] {
    border-color: ${outline};
    background-color: ${outline};
  }

  ${(p) =>
    p.$variant === 'compact' &&
    `
    border: 1px solid ${outline};
    width: 16px;
    height: 16px;

    &[data-state='checked'] {
      border-color: ${outline};
      background-color: ${surfaceBright};
    }
  `}
`

const StyledIndicator = styled(Indicator)<{ $variant?: 'default' | 'compact' }>`
  transform: scale(0);
  opacity: 0;
  border-radius: 100%;
  background-color: ${surfaceBright};
  width: 6px;
  height: 6px;

  &[data-state='checked'] {
    transform: scale(1);
    transition: all 0.05s ease-in;
    opacity: 1;
  }

  ${(p) =>
    p.$variant === 'compact' &&
    `
    width: 8px;
    height: 8px;

    &[data-state='checked'] {
      background-color: ${onSurface};
    }
  `}
`

const Label = styled.label<{ $variant?: 'default' | 'compact' }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  color: ${(p) => (p.$variant === 'compact' ? onSurface : 'inherit')};
  font-size: ${(p) => (p.$variant === 'compact' ? '14px' : 'inherit')};
  font-weight: ${(p) => (p.$variant === 'compact' ? 500 : 'inherit')};
`

const Description = styled.span`
  opacity: 0.7;
  color: ${onSurfaceVariant};
  font-size: 14px;
`
